import { useQuery } from "@tanstack/react-query";
import { fetchSessionAvailability } from "@/lib/api";
import { Users } from "lucide-react";

interface AvailabilitySummaryProps {
  sessionId: number;
}

export function AvailabilitySummary({ sessionId }: AvailabilitySummaryProps) {
  // staleTime: 0 overrides the app-wide default (queryClient.ts sets
  // staleTime: Infinity globally) — one of these renders per session card on
  // CoachSchedule.tsx (N+1, not batched), and each shows this specific
  // session's yes/no/maybe counts, which change the moment a parent RSVPs.
  // With the global default, a coach tab left open here would keep showing
  // whatever counts were fetched on first render, no matter how many
  // parents respond afterward, until a full page reload. Same fix already
  // applied to BatchesPage.tsx's main query for the same reason.
  const { data, isLoading } = useQuery({
    queryKey: ["/api/sessions", sessionId, "availability"],
    queryFn: () => fetchSessionAvailability(sessionId),
    staleTime: 0,
  });

  if (isLoading) {
    return (
      <div className="text-xs text-muted-foreground flex items-center gap-1">
        <Users className="h-3 w-3" />
        <span>Loading...</span>
      </div>
    );
  }

  if (!data || !data.ok) {
    return null;
  }

  const { summary } = data;

  return (
    <div
      className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap"
      data-testid={`coach-availability-summary-${sessionId}`}
    >
      <Users className="h-3 w-3" />
      <span className="text-green-600 font-medium">Going: {summary.yes}</span>
      <span className="text-muted-foreground">|</span>
      <span className="text-red-600 font-medium">Not Going: {summary.no}</span>
      <span className="text-muted-foreground">|</span>
      <span className="text-yellow-600 font-medium">Unsure: {summary.maybe}</span>
    </div>
  );
}
